import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserX, Plus, Ban, CheckCircle2, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  absence: "Absence", retard: "Retard",
  conge_maladie: "Congé maladie", conge_annuel: "Congé annuel", autre: "Autre",
};

export default function AbsencesAdminPage() {
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", date: new Date().toISOString().slice(0, 10),
    absence_type: "absence", justified: false, justification: "", hours_missed: 0,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [a, u] = await Promise.all([
        api.get("/erp/absences"),
        api.get("/users?role=teacher"),
      ]);
      setAbsences(a.data);
      // Get all staff (teacher + admin + finance)
      const staff = await Promise.all([
        api.get("/users?role=admin"),
        api.get("/users?role=finance"),
        api.get("/users?role=director"),
      ]);
      const all = [...u.data, ...staff[0].data, ...staff[1].data, ...staff[2].data];
      setEmployees(all);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.employee_id) return toast.error("Sélectionnez un employé");
    setSaving(true);
    try {
      await api.post("/erp/absences", { ...form, hours_missed: parseFloat(form.hours_missed) || 0 });
      toast.success("Absence enregistrée");
      setOpen(false);
      setForm({ employee_id: "", date: new Date().toISOString().slice(0, 10), absence_type: "absence", justified: false, justification: "", hours_missed: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const toggleJustified = async (abs) => {
    try {
      await api.patch(`/erp/absences/${abs.id}`, null, { params: { justified: !abs.justified } });
      toast.success("Mis à jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const suspend = async (emp_id, name) => {
    if (!window.confirm(`Suspendre le compte de ${name} ?`)) return;
    try {
      await api.post(`/erp/employees/${emp_id}/suspend`, null, { params: { reason: "Absences répétées non justifiées" } });
      toast.success("Compte suspendu");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const reactivate = async (emp_id) => {
    try {
      await api.post(`/erp/employees/${emp_id}/reactivate`);
      toast.success("Compte réactivé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Group unjustified counts per employee
  const unjustifiedCounts = {};
  absences.forEach(a => {
    if (!a.justified) unjustifiedCounts[a.employee_id] = (unjustifiedCounts[a.employee_id] || 0) + 1;
  });

  const empMap = Object.fromEntries(employees.map(e => [e.id, e]));

  return (
    <div className="space-y-6" data-testid="absences-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><UserX className="h-3 w-3" /><span>SUIVI RH</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Absences employés</h1>
          <p className="text-slate-500 mt-1">Enregistrement et suivi des absences. Notification automatique après 2 non justifiées.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
          <Button onClick={() => setOpen(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-absence-btn">
            <Plus className="h-4 w-4" />Nouvelle absence
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {Object.entries(unjustifiedCounts).filter(([, c]) => c >= 2).length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4">
          <div className="font-heading font-bold text-red-800 mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Employés avec 2+ absences non justifiées</div>
          <div className="space-y-1">
            {Object.entries(unjustifiedCounts).filter(([, c]) => c >= 2).map(([id, c]) => {
              const e = empMap[id];
              const name = e ? `${e.first_name} ${e.last_name}` : id;
              const isSuspended = e?.status === "suspended";
              const isFounder = e?.role === "founder";
              return (
                <div key={id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm">
                  <span>{name} — <span className="font-bold text-red-700">{c} non justifiées</span></span>
                  {isFounder ? (
                    <span className="text-xs text-slate-500">Fondateur (protégé)</span>
                  ) : isSuspended ? (
                    <Button size="sm" variant="outline" onClick={() => reactivate(id)} className="gap-1 text-xs"><CheckCircle2 className="h-3 w-3" />Réactiver</Button>
                  ) : (
                    <Button size="sm" variant="destructive" onClick={() => suspend(id, name)} className="gap-1 text-xs" data-testid={`suspend-${id}`}><Ban className="h-3 w-3" />Suspendre</Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Employé</th>
                <th className="px-4 py-3 text-left">Rôle</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-right">Heures</th>
                <th className="px-4 py-3 text-center">Justifiée</th>
                <th className="px-4 py-3 text-left">Motif</th>
              </tr>
            </thead>
            <tbody>
              {absences.map(a => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`abs-row-${a.id}`}>
                  <td className="px-4 py-2 font-medium">{a.employee_name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 uppercase">{a.employee_role}</td>
                  <td className="px-4 py-2">{new Date(a.date).toLocaleDateString("fr-FR")}</td>
                  <td className="px-4 py-2">{TYPE_LABELS[a.absence_type] || a.absence_type}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{a.hours_missed || 0}h</td>
                  <td className="px-4 py-2 text-center">
                    <button onClick={() => toggleJustified(a)} className={`text-xs px-2 py-1 rounded-full font-semibold ${a.justified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`} data-testid={`toggle-just-${a.id}`}>
                      {a.justified ? "OUI" : "NON"}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate">{a.justification || "—"}</td>
                </tr>
              ))}
              {absences.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune absence enregistrée</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle absence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employé *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="abs-employee"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="abs-date" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.absence_type} onValueChange={(v) => setForm({ ...form, absence_type: v })}>
                  <SelectTrigger data-testid="abs-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Heures manquées</Label>
                <Input type="number" step="0.5" value={form.hours_missed} onChange={e => setForm({ ...form, hours_missed: e.target.value })} data-testid="abs-hours" />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.justified} onChange={e => setForm({ ...form, justified: e.target.checked })} data-testid="abs-justified" />
                  Justifiée
                </label>
              </div>
            </div>
            <div>
              <Label>Justification</Label>
              <Textarea value={form.justification} onChange={e => setForm({ ...form, justification: e.target.value })} rows={2} placeholder="Motif fourni par l'employé..." data-testid="abs-justification" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="abs-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
